import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Copy, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

const COL_MODES = ["Standard", "Single Subject Exam"];
const emptyForm = { name: "", group_id: "", conducted: true, practical: false, subject_columns: "Standard", default_max: 100 };

export default function AdvancedExamSheet({ open, onOpenChange, classes, session, onSaved }) {
  const [f, setF] = useState(emptyForm);
  const [groups, setGroups] = useState([]);
  const [sel, setSel] = useState([]); // selected class ids
  const [perClass, setPerClass] = useState(false);
  const [classSubs, setClassSubs] = useState({}); // class_id -> [{subject_id, subject_name, max_marks}]
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setF(emptyForm); setSel([]); setPerClass(false); setClassSubs({});
    if (session) api.get(`/exam-groups?session=${encodeURIComponent(session)}`).then((r) => setGroups(r.data)).catch(() => setGroups([]));
  }, [open, session]);

  const availClasses = classes.filter((c) => !session || !c.academic_session || c.academic_session === session);
  const clsLabel = (c) => `${c.name}${c.section ? " " + c.section : ""}`;

  const fetchSubs = async (cid) => {
    if (classSubs[cid]) return;
    try {
      const r = await api.get(`/classes/${cid}/subjects`);
      const subs = (r.data.subjects || []).map((s) => ({ subject_id: s.id, subject_name: s.name, max_marks: f.default_max }));
      setClassSubs((p) => ({ ...p, [cid]: subs }));
    } catch (e) { setClassSubs((p) => ({ ...p, [cid]: [] })); }
  };

  const toggleClass = (cid) => {
    setSel((p) => {
      const next = p.includes(cid) ? p.filter((x) => x !== cid) : [...p, cid];
      if (!p.includes(cid) && perClass) fetchSubs(cid);
      return next;
    });
  };

  const togglePerClass = (checked) => {
    setPerClass(!!checked);
    if (checked) sel.forEach((cid) => fetchSubs(cid));
  };

  const setSubMax = (cid, i, val) => setClassSubs((p) => ({ ...p, [cid]: p[cid].map((s, idx) => idx === i ? { ...s, max_marks: val } : s) }));
  const copyMaxToClass = (cid, i) => setClassSubs((p) => { const v = p[cid][i].max_marks; return { ...p, [cid]: p[cid].map((s) => ({ ...s, max_marks: v })) }; });

  const save = async () => {
    if (!f.name.trim()) { toast.error("Exam name is required"); return; }
    if (sel.length === 0) { toast.error("Select at least one class"); return; }
    if (perClass) {
      const empties = sel.filter((cid) => (classSubs[cid] || []).length === 0);
      if (empties.length) toast.warning("Some selected classes have no subjects; their exams will be created empty.");
    }
    const grp = groups.find((g) => g.id === f.group_id);
    const payload = {
      name: f.name.trim(), group_id: f.group_id, exam_group: grp?.name || "",
      academic_session: session || "", conducted: f.conducted, practical: f.practical,
      subject_columns: f.subject_columns, default_max: Number(f.default_max) || 100, class_ids: sel,
      per_class: perClass ? sel.map((cid) => ({
        class_id: cid,
        subjects: (classSubs[cid] || []).map((s) => ({ subject_id: s.subject_id, subject_name: s.subject_name, max_marks: Number(s.max_marks) || 0, pass_marks: Math.round((Number(s.max_marks) || 0) * 0.33), eval_type: "marks" })),
      })) : [],
    };
    setSaving(true);
    try {
      const r = await api.post("/exams/advanced", payload);
      toast.success(`Created ${r.data.count} exam${r.data.count === 1 ? "" : "s"}`);
      onOpenChange(false); onSaved?.();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-none sm:w-[55vw] overflow-y-auto flex flex-col" data-testid="adv-exam-dialog">
        <SheetHeader><SheetTitle>Create Advanced Exam</SheetTitle></SheetHeader>
        <div className="space-y-5 mt-4 flex-1">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Exam Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. First Term Exam" data-testid="adv-name" className="mt-1" /></div>
            <div><Label>Group Name</Label>
              <Select value={f.group_id} onValueChange={(v) => setF({ ...f, group_id: v })}>
                <SelectTrigger className="mt-1" data-testid="adv-group"><SelectValue placeholder="Select group / term" /></SelectTrigger>
                <SelectContent>{groups.length === 0 ? <div className="px-3 py-2 text-xs text-slate-400">No groups — add in the Groups tab</div> : groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Subject Columns</Label>
              <Select value={f.subject_columns} onValueChange={(v) => setF({ ...f, subject_columns: v })}>
                <SelectTrigger className="mt-1" data-testid="adv-columns"><SelectValue /></SelectTrigger>
                <SelectContent>{COL_MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Maximum Marks (per subject)</Label><Input type="number" value={f.default_max} onChange={(e) => setF({ ...f, default_max: e.target.value })} className="mt-1" data-testid="adv-maxmarks" /></div>
          </div>

          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={f.conducted} onCheckedChange={(c) => setF({ ...f, conducted: !!c })} data-testid="adv-conducted" />Exam Conducted</label>
            <label className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={f.practical} onCheckedChange={(c) => setF({ ...f, practical: !!c })} data-testid="adv-practical" />Practical Column</label>
          </div>

          <div>
            <Label>Select Classes</Label>
            <div className="mt-1.5 flex flex-wrap gap-2" data-testid="adv-classes">
              {availClasses.length === 0 ? <span className="text-xs text-slate-400">No classes for this session.</span> : availClasses.map((c) => {
                const on = sel.includes(c.id);
                return (
                  <button key={c.id} type="button" onClick={() => toggleClass(c.id)} data-testid={`adv-class-${c.id}`}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-colors ${on ? "bg-indigo-600 border-indigo-600 text-white" : "border-slate-300 text-slate-600 hover:border-indigo-400"}`}>
                    {on && <Check size={14} />}{clsLabel(c)}
                  </button>
                );
              })}
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer border-t border-slate-100 pt-4">
            <Checkbox checked={perClass} onCheckedChange={togglePerClass} data-testid="adv-perclass" />
            Do you want to select subjects for each class and section?
          </label>

          {perClass && (
            <div className="space-y-3" data-testid="adv-perclass-panel">
              {sel.length === 0 && <p className="text-xs text-slate-400">Select classes above to configure their subjects.</p>}
              {sel.map((cid) => {
                const c = classes.find((x) => x.id === cid);
                const subs = classSubs[cid];
                return (
                  <div key={cid} className="rounded-lg border border-slate-200" data-testid={`adv-class-block-${cid}`}>
                    <div className="px-3 py-2 bg-slate-50 text-sm font-semibold text-slate-700 border-b border-slate-200">{c ? clsLabel(c) : cid}</div>
                    <div className="p-3 space-y-2">
                      {subs === undefined ? <div className="py-3 flex justify-center"><Loader2 className="animate-spin text-indigo-400" size={18} /></div> :
                        subs.length === 0 ? <p className="text-xs text-slate-400">No subjects assigned to this class.</p> :
                        subs.map((s, i) => (
                          <div key={s.subject_id} className="flex items-center gap-2">
                            <span className="flex-1 text-sm text-slate-700">{s.subject_name}</span>
                            <Input type="number" value={s.max_marks} onChange={(e) => setSubMax(cid, i, e.target.value)} className="h-8 w-24" data-testid={`adv-submax-${cid}-${i}`} />
                            <Button type="button" size="sm" variant="outline" className="h-8 gap-1" onClick={() => copyMaxToClass(cid, i)} data-testid={`adv-copy-${cid}-${i}`}><Copy size={13} />Copy</Button>
                          </div>
                        ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <SheetFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="gap-1.5" data-testid="adv-save">{saving ? <Loader2 size={15} className="animate-spin" /> : null}Create Exam</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
